import { lazy, Suspense, useEffect, useState } from "react";
import Lenis from "lenis";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import Seo from "@/components/Seo";
import Intro from "@/components/landing/Intro";
import LaunchPopup from "@/components/landing/LaunchPopup";
import Nav from "@/components/landing/Nav";
import Hero from "@/components/landing/Hero";
import Marquee from "@/components/landing/Marquee";
import Manifesto from "@/components/landing/Manifesto";
import Reach from "@/components/landing/Reach";
import Pitch from "@/components/landing/Pitch";
import Team from "@/components/landing/Team";
import Faq from "@/components/landing/Faq";
import Contact from "@/components/landing/Contact";
import Footer from "@/components/landing/Footer";
import ScrollRocket from "@/components/landing/ScrollRocket";
import InstallPrompt from "@/components/landing/InstallPrompt";
import Analytics from "@/components/Analytics";
import site from "@/seo/site.json";
import { primeSoundLogo } from "@/components/landing/sound";
import { scrollToId } from "@/components/landing/Nav";

const loadTrailer = () => import("@/components/landing/Trailer");
const loadPitchRoom = () => import("@/components/landing/PitchRoom");
const Trailer = lazy(loadTrailer);
const PitchRoom = lazy(loadPitchRoom);
const Concierge = lazy(() => import("@/components/landing/Concierge"));
const Admin = lazy(() => import("@/pages/Admin"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const AudiencePage = lazy(() => import("@/pages/AudiencePage"));

const Landing = () => {
  const [introDone, setIntroDone] = useState(false);

  useEffect(() => {
    const { hash } = window.location;
    if (!hash || !introDone) return;
    let scrolledAt = 0;
    let t;
    // Make sure lazy sections above the target are mounted before measuring.
    Promise.all([loadPitchRoom(), loadTrailer()]).then(() => {
      t = setTimeout(() => {
        scrolledAt = Date.now();
        scrollToId(hash);
      }, 350);
    });
    const realign = () => {
      const el = document.querySelector(hash);
      if (!el) return;
      const offset = document.querySelector("header[data-testid='main-nav']")?.offsetHeight || 72;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      if (window.__lenis) window.__lenis.scrollTo(top, { immediate: true });
      else window.scrollTo(0, top);
    };
    // Late layout growth (images, videos) after the smooth scroll finished — snap back onto the target.
    const ro = new ResizeObserver(() => {
      if (scrolledAt && Date.now() - scrolledAt > 1900) realign();
    });
    ro.observe(document.body);
    // Browsers also retry native fragment scrolling until load — verify and snap after the animation.
    const checks = [2200, 3600, 5200].map((ms) =>
      setTimeout(() => {
        const el = document.querySelector(hash);
        if (!el) return;
        const offset = document.querySelector("header[data-testid='main-nav']")?.offsetHeight || 72;
        if (Math.abs(el.getBoundingClientRect().top - offset) > 8) realign();
      }, ms)
    );
    const stop = setTimeout(() => ro.disconnect(), 7000);
    return () => {
      clearTimeout(t);
      clearTimeout(stop);
      checks.forEach(clearTimeout);
      ro.disconnect();
    };
  }, [introDone]);

  return (
    <>
      <Seo title={site.title} siteName={site.name} description={site.description} image={site.ogImage} />
      <Intro onDone={() => setIntroDone(true)} />
      <LaunchPopup />
      <InstallPrompt />
      <ScrollRocket />
      <Suspense fallback={null}>
        <Concierge />
      </Suspense>
      <Nav />
      <main>
        <Hero active={introDone} />
        <Marquee />
        <Manifesto />
        <Reach />
        <Pitch />
        <Suspense fallback={null}>
          <PitchRoom />
          <Trailer />
        </Suspense>
        <Team />
        <Faq />
        <Contact />
      </main>
      <Footer />
    </>
  );
};

const AdminPage = () => (
  <>
    <Seo title="Control Room — Launchpad TV" robots="noindex,nofollow" />
    <Admin />
  </>
);

function App() {
  useEffect(() => {
    const prime = () => primeSoundLogo();
    window.addEventListener("pointerdown", prime, { once: true });
    window.addEventListener("keydown", prime, { once: true });
    const lenis = new Lenis({ duration: 1.25, smoothWheel: true });
    window.__lenis = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      window.removeEventListener("pointerdown", prime);
      window.removeEventListener("keydown", prime);
      cancelAnimationFrame(rafId);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <div className="App bg-obsidian text-foreground min-h-screen">
      <BrowserRouter>
        <Analytics />
        <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/:slug" element={<AudiencePage />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
      <Toaster theme="dark" position="bottom-right" />
    </div>
  );
}

export default App;
